import type {Segment} from "@/tabletop/visibility/VisibilityGeometry.ts";
import type {WorldPoint} from "@/dungeons/model/DungeonGeometry.ts";
import {lightSourceDirection, type LightingBounds} from "./LightingModel.ts";

/** Clip occluders to the map: the directional source is outside this boundary, not outside the viewport. */
export function clipLightingSegment(segment: Segment, bounds: LightingBounds): Segment | undefined {
    const dx = segment.end.x - segment.start.x;
    const dy = segment.end.y - segment.start.y;
    let start = 0;
    let end = 1;
    for (const [p, q] of [[-dx, segment.start.x - bounds.minX], [dx, bounds.maxX - segment.start.x],
        [-dy, segment.start.y - bounds.minY], [dy, bounds.maxY - segment.start.y]]) {
        if (Math.abs(p) < 1e-10) {if (q < 0) return; continue;}
        const value = q / p;
        if (p < 0) start = Math.max(start, value);
        else end = Math.min(end, value);
        if (start > end) return;
    }
    return {start: {x: segment.start.x + dx * start, y: segment.start.y + dy * start},
        end: {x: segment.start.x + dx * end, y: segment.start.y + dy * end}};
}

/** Two triangles per blocker, extruded away from the source beyond the entire map. GPU rasterizes their union. */
export function directionalShadowTriangles(bounds: LightingBounds, blockers: readonly Segment[], degrees: number): WorldPoint[] {
    const source = lightSourceDirection(degrees);
    const distance = Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) * 2 + 1;
    const extend = (point: WorldPoint): WorldPoint => ({x: point.x - source.x * distance, y: point.y - source.y * distance});
    return blockers.flatMap(blocker => {
        const clipped = clipLightingSegment(blocker, bounds);
        if (!clipped) return [];
        const {start, end} = clipped;
        const farStart = extend(start);
        const farEnd = extend(end);
        return [start, end, farEnd, start, farEnd, farStart];
    });
}
